import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import { ArrowDownLeft, ArrowUpRight, ArrowLeftRight, Activity, FileDown, RefreshCw, BookUp } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const TYPE_META = {
  inflow: { label: "Inflow", icon: ArrowDownLeft, cls: "bg-primary/15 text-primary" },
  outflow: { label: "Outflow", icon: ArrowUpRight, cls: "bg-destructive/15 text-destructive" },
  transfer: { label: "Transfer", icon: ArrowLeftRight, cls: "bg-accent/15 text-accent" },
};

const STATUS_CLS = {
  completed: "text-primary",
  failed: "text-destructive",
  retried: "text-muted-foreground",
};

const LEDGER_CLS = { posted: "text-primary", failed: "text-destructive", pending: "text-muted-foreground" };
const LEDGER_LABEL = { posted: "Posted", failed: "Failed", pending: "Pending", skipped: "Skipped", not_sent: "Not sent" };

export default function Transactions() {
  const [txns, setTxns] = useState([]);
  const [type, setType] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [retrying, setRetrying] = useState(null);
  const [reposting, setReposting] = useState(null);

  const repost = async (t) => {
    setReposting(t.id);
    try {
      const r = await api.post(`/transactions/${t.id}/ledger`);
      if (r.data.ledger_status === "posted") toast.success("Posted to CITIDAN ledger");
      else toast.error(`Ledger post failed: ${r.data.ledger_message}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setReposting(null);
    }
  };

  const load = useCallback(() => {
    const params = {};
    if (type !== "all") params.type = type;
    if (start) params.start = start;
    if (end) params.end = end;
    api.get("/transactions", { params }).then((r) => setTxns(r.data.transactions)).catch(() => {});
  }, [type, start, end]);

  useEffect(() => { load(); }, [load]);

  const downloadReceipt = async (t) => {
    try {
      const res = await api.get(`/transactions/${t.id}/receipt`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `citidan_receipt_${t.id.slice(-8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Receipt downloaded");
    } catch {
      toast.error("Could not generate receipt");
    }
  };

  const retry = async (t) => {
    setRetrying(t.id);
    try {
      const res = await api.post(`/transactions/${t.id}/retry`);
      if (res.data.status === "failed") {
        toast.error(`Retry failed: ${res.data.detail || "processor declined"}`);
      } else {
        toast.success("Transaction retried successfully");
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setRetrying(null);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-primary">Unified Ledger</div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">Transactions</h1>
          <p className="mt-2 text-sm text-muted-foreground">All inflow, outflow and transfer activity across your accounts.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-36" data-testid="txn-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="inflow">Inflow</SelectItem>
              <SelectItem value="outflow">Outflow</SelectItem>
              <SelectItem value="transfer">Transfer</SelectItem>
            </SelectContent>
          </Select>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" data-testid="txn-start" aria-label="Start date" />
          <span className="text-xs text-muted-foreground">to</span>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" data-testid="txn-end" aria-label="End date" />
          {(start || end) && <Button variant="ghost" size="sm" onClick={() => { setStart(""); setEnd(""); }} data-testid="txn-clear">Clear</Button>}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-6" data-testid="txn-list">
        <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground">
          <Activity className="h-4 w-4 text-primary" /> {txns.length} transactions
        </div>
        {txns.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">No transactions match these filters.</p>
        ) : (
          <div className="divide-y divide-border">
            {txns.map((t) => {
              const meta = TYPE_META[t.type] || TYPE_META.transfer;
              const Icon = meta.icon;
              const sign = t.type === "inflow" ? "+" : t.type === "outflow" ? "−" : "";
              return (
                <div key={t.id} className="flex items-center gap-3 py-3" data-testid={`txn-${t.id}`}>
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${meta.cls}`}><Icon className="h-4 w-4" /></span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider ${meta.cls}`}>{meta.label}</span>
                      <span className="text-xs text-muted-foreground capitalize">{t.account_type} · {t.mode}</span>
                      {t.processor && <span className="text-[10px] text-muted-foreground capitalize">· {t.processor}</span>}
                    </div>
                    <div className="mt-0.5 truncate text-sm text-muted-foreground">{t.source} → {t.destination}</div>
                    {t.reference && <div className="truncate font-mono text-[10px] text-muted-foreground/80" data-testid={`txn-ref-${t.id}`}>Ref: {t.reference}</div>}
                    <div className="mt-0.5 text-[10px]" title={t.ledger_message} data-testid={`txn-ledger-${t.id}`}>
                      <span className="text-muted-foreground">CITIDAN ledger: </span>
                      <span className={LEDGER_CLS[t.ledger_status] || "text-muted-foreground"}>{LEDGER_LABEL[t.ledger_status] || t.ledger_status}</span>
                      {t.ledger_status === "failed" && t.ledger_message && <span className="text-muted-foreground"> · {t.ledger_message} · {t.ledger_attempts} attempt{t.ledger_attempts === 1 ? "" : "s"}, auto-retries hourly</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right">
                      <div className={`font-semibold ${t.type === "inflow" ? "text-primary" : t.type === "outflow" ? "text-destructive" : "text-foreground"}`}>{sign}${t.amount.toFixed(2)}</div>
                      <div className="text-[11px] text-muted-foreground">
                        <span className={`capitalize ${STATUS_CLS[t.status] || ""}`}>{t.status}</span> · {new Date(t.created_at).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      {(t.ledger_status === "failed" || t.ledger_status === "pending") && (
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Re-post to CITIDAN ledger"
                          disabled={reposting === t.id} onClick={() => repost(t)} data-testid={`txn-ledger-repost-${t.id}`}>
                          <BookUp className={`h-3.5 w-3.5 ${reposting === t.id ? "animate-pulse" : ""}`} />
                        </Button>
                      )}
                      {t.can_retry && (
                        <Button variant="outline" size="icon" className="h-8 w-8" title="Retry transaction"
                          disabled={retrying === t.id} onClick={() => retry(t)} data-testid={`txn-retry-${t.id}`}>
                          <RefreshCw className={`h-3.5 w-3.5 ${retrying === t.id ? "animate-spin" : ""}`} />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Download receipt"
                        onClick={() => downloadReceipt(t)} data-testid={`txn-receipt-${t.id}`}>
                        <FileDown className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
